import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTeamContext } from "../../../contexts/TeamContext";
import { JourneyStepInput, Journey } from "../../../types/journey";
import { JourneyContentRow, GameName } from "../../../types/games";
import { Category } from "../../../types/games";
import { GameType } from "../../../hooks/useGameTypes";
import { useCreateJourney } from "../../../hooks/useJourneys";
import JourneyTemplatesList from "../../journeys/JourneyTemplatesList";

// Import content conversion utilities
import {
  contentItemsToRows,
  rowsToContentItems,
} from "../../../utils/contentFormatters";

import WizardContainer from "./WizardContainer";
import JourneyBasicInfoStep, { JourneyBasicInfo } from "./JourneyBasicInfoStep";
import TouchpointStep from "./TouchpointStep";
import ReviewStep from "./ReviewStep";

// Definition of wizard state that will be managed by parent
export interface WizardState {
  currentStep: number;
  journeyInfo: JourneyBasicInfo;
  touchpoints: JourneyStepInput[];
  // Store content rows for each touchpoint to maintain a single source of truth
  contentRows: { [touchpointId: string]: JourneyContentRow[] };
  // Selected template (if any)
  selectedTemplate?: Journey | null;
}

interface JourneyWizardProps {
  categories: Category[];
  gameTypes: GameType[];
  categoriesLoading?: boolean;
  gameTypesLoading?: boolean;
  wizardState: WizardState;
  onWizardStateChange: (state: WizardState) => void;
  onCancel: () => void;
  onComplete: (
    touchpoints?: JourneyStepInput[],
    skipNavigation?: boolean,
  ) => void;
  onDelete?: () => void; // Optional callback for journey deletion
  isEdit?: boolean;
}

/**
 * JourneyWizard component
 *
 * Manages the entire journey creation process through a step-by-step wizard interface.
 * This improves the user experience by focusing on one aspect of configuration at a time.
 */
const JourneyWizard: React.FC<JourneyWizardProps> = ({
  categories,
  gameTypes,
  categoriesLoading = false,
  gameTypesLoading = false,
  wizardState,
  onWizardStateChange,
  onCancel,
  onComplete,
  onDelete,
  isEdit = false,
}) => {
  const { teamId } = useTeamContext();

  // Use createJourney mutation for API integration
  const createJourneyMutation = useCreateJourney();

  // Template selection is disabled - we go directly to journey creation
  // This can be made configurable via props if needed in the future
  const [showTemplateStep] = useState(false);

  // Extract wizard state from props
  const { currentStep, journeyInfo, touchpoints, selectedTemplate } =
    wizardState;

  // Ensure contentRows always exists as an object, even if not provided in wizardState
  const contentRows = wizardState.contentRows || {};

  // Local state for submission and errors
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // We'll manage the expanded state inside the TouchpointManager component

  // Calculate total steps (1 for template selection if enabled + 1 for basics + 1 per touchpoint + 1 for review)
  const getTotalSteps = useCallback(
    () => (showTemplateStep ? 1 : 0) + 1 + journeyInfo.touchpointCount + 1,
    [journeyInfo.touchpointCount, showTemplateStep],
  );

  // Since template step is skipped, basic info is always the first step
  const templateStepOffset = 0;
  const basicInfoStepIndex = 0;

  // Determine if current step is a touchpoint configuration step
  const isTouchpointStep =
    currentStep > basicInfoStepIndex && currentStep < getTotalSteps() - 1;

  // Get the current touchpoint index being edited
  const getCurrentTouchpointIndex = useCallback(
    () => (isTouchpointStep ? currentStep - 1 - templateStepOffset : -1),
    [isTouchpointStep, currentStep, templateStepOffset],
  );

  // Check if we're on the review step
  const isReviewStep = currentStep === getTotalSteps() - 1;

  // Initialize contentRows from touchpoints if needed
  useEffect(() => {
    // Skip if contentRows is already initialized
    if (contentRows && Object.keys(contentRows).length > 0) return;

    // Initialize content rows for touchpoints that have custom_content
    const initializedContentRows: {
      [touchpointId: string]: JourneyContentRow[];
    } = {};

    touchpoints.forEach((touchpoint, index) => {
      if (touchpoint.custom_content && touchpoint.custom_content.length > 0) {
        initializedContentRows[index.toString()] = contentItemsToRows(
          touchpoint.custom_content,
        );
      }
    });

    // Only update if we found any content to initialize
    if (Object.keys(initializedContentRows).length > 0) {
      onWizardStateChange({
        ...wizardState,
        contentRows: initializedContentRows,
      });
    }
  }, [touchpoints, contentRows, wizardState, onWizardStateChange]);

  // Update journey basic info
  const handleJourneyInfoChange = useCallback(
    (info: JourneyBasicInfo) => {
      // Create new state with updated journey info
      const newState = { ...wizardState, journeyInfo: info };

      // Adjust touchpoints array if count changes
      if (info.touchpointCount !== touchpoints.length) {
        const newTouchpoints = [...touchpoints];

        if (info.touchpointCount > touchpoints.length) {
          // Add more touchpoints
          for (let i = touchpoints.length; i < info.touchpointCount; i++) {
            newTouchpoints.push({
              game_type_id: "",
              offset_days: i, // Space them out by default
              offset_hours: 9, // Default to 9 AM
              offset_minutes: 0,
              content_selection_type: "surprise_me",
            });
          }
        } else {
          // Remove excess touchpoints
          newState.touchpoints = touchpoints.slice(0, info.touchpointCount);
          return onWizardStateChange(newState);
        }

        newState.touchpoints = newTouchpoints;
      }

      onWizardStateChange(newState);
    },
    [wizardState, touchpoints.length, onWizardStateChange],
  );

  // Update a specific touchpoint
  const handleTouchpointChange = useCallback(
    (updatedTouchpoint: JourneyStepInput) => {
      const index = getCurrentTouchpointIndex();
      if (index >= 0) {
        const newTouchpoints = [...touchpoints];
        newTouchpoints[index] = updatedTouchpoint;

        onWizardStateChange({
          ...wizardState,
          touchpoints: newTouchpoints,
        });
      }
    },
    [getCurrentTouchpointIndex, touchpoints, wizardState, onWizardStateChange],
  );

  // Handle content rows changes for a specific touchpoint
  // This ensures all content row state lives in the parent component
  const handleContentRowsChange = useCallback(
    (touchpointId: string, newRows: JourneyContentRow[]) => {
      // Ensure contentRows is valid to avoid "Cannot read properties of undefined" errors
      const safeContentRows = contentRows || {};

      // Update the content rows for the specific touchpoint
      const updatedContentRows = {
        ...safeContentRows,
        [touchpointId]: newRows,
      };

      // Get the game type name for the touchpoint
      const targetTouchpoint = touchpoints.find(
        (_, idx) => idx.toString() === touchpointId,
      );
      const gameType = gameTypes.find(
        (g) => g.id === targetTouchpoint?.game_type_id,
      );
      const gameName = (gameType?.name as GameName) || null;

      // Convert rows to content items for the touchpoint
      // If no game name is found, we'll get an empty array which is safer than undefined
      const contentItems = gameName
        ? rowsToContentItems(newRows, gameName)
        : [];

      // Find and update the corresponding touchpoint
      const index = touchpoints.findIndex(
        (_, idx) => idx.toString() === touchpointId,
      );
      if (index >= 0) {
        const newTouchpoints = [...touchpoints];
        newTouchpoints[index] = {
          ...newTouchpoints[index],
          custom_content: contentItems,
        };

        // Update the wizard state with both changes
        onWizardStateChange({
          ...wizardState,
          touchpoints: newTouchpoints,
          contentRows: updatedContentRows,
        });
      } else {
        // Just update the content rows if touchpoint isn't found
        onWizardStateChange({
          ...wizardState,
          contentRows: updatedContentRows,
        });
      }
    },
    [wizardState, touchpoints, contentRows, onWizardStateChange],
  );

  // Reference to active step component for collecting state before navigation
  const activeStepRef = useRef<{
    collectState?: () => any;
  }>({});

  // Register a step component's collectState function
  const registerStepStateCollector = useCallback((collector: () => any) => {
    activeStepRef.current.collectState = collector;
  }, []);

  // Collect state from the current step before navigating
  const collectCurrentStepState = useCallback(() => {
    if (activeStepRef.current.collectState) {
      const collectedState = activeStepRef.current.collectState();

      // Apply the collected state based on current step
      if (currentStep === 0) {
        // Basic info step
        onWizardStateChange({
          ...wizardState,
          journeyInfo: collectedState,
        });
      } else if (isTouchpointStep) {
        // Touchpoint config step
        const touchpointIndex = getCurrentTouchpointIndex();
        if (touchpointIndex >= 0) {
          const newTouchpoints = [...touchpoints];
          newTouchpoints[touchpointIndex] = collectedState;
          onWizardStateChange({
            ...wizardState,
            touchpoints: newTouchpoints,
          });
        }
      }
    }
  }, [
    currentStep,
    getCurrentTouchpointIndex,
    isTouchpointStep,
    onWizardStateChange,
    touchpoints,
    wizardState,
  ]);

  // Navigation handlers with state collection
  const handleNext = useCallback(() => {
    // First collect current step state
    collectCurrentStepState();

    // Then navigate to next step
    onWizardStateChange({
      ...wizardState,
      currentStep: Math.min(currentStep + 1, getTotalSteps() - 1),
    });
  }, [
    wizardState,
    currentStep,
    onWizardStateChange,
    getTotalSteps,
    collectCurrentStepState,
  ]);

  const handlePrevious = useCallback(() => {
    // First collect current step state
    collectCurrentStepState();

    // Then navigate to previous step
    onWizardStateChange({
      ...wizardState,
      currentStep: Math.max(currentStep - 1, 0),
    });
  }, [wizardState, currentStep, onWizardStateChange, collectCurrentStepState]);

  const handleEditJourneyInfo = useCallback(() => {
    // First collect current step state
    collectCurrentStepState();

    // Then navigate to info step
    onWizardStateChange({
      ...wizardState,
      currentStep: 0,
    });
  }, [wizardState, onWizardStateChange, collectCurrentStepState]);

  const handleEditTouchpoint = useCallback(
    (index: number) => {
      onWizardStateChange({
        ...wizardState,
        currentStep: index + 1,
      });
    },
    [wizardState, onWizardStateChange],
  );

  // Submit the journey with custom content to API
  const handleSubmit = async () => {
    if (!teamId) {
      setError("No team ID available");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Process touchpoints to include custom content where appropriate
      const processedTouchpoints = touchpoints.map((touchpoint, index) => {
        // Create a copy of the touchpoint
        const touchpointWithContent = { ...touchpoint };

        // If this touchpoint has custom content, process it
        if (
          touchpoint.content_selection_type === "custom" &&
          contentRows[index.toString()] &&
          contentRows[index.toString()].length > 0
        ) {
          // Convert ContentRows to ContentItems for API
          // rowsToContentItems expects: rows, gameName, and optionally touchpointId

          // Find the game type object using the game_type_id from the touchpoint
          const gameType = gameTypes.find(
            (gt) => gt.id === touchpoint.game_type_id,
          );

          // If we have a game type, use its name for the conversion, otherwise fallback to a default
          if (gameType?.name) {
            touchpointWithContent.custom_content = rowsToContentItems(
              contentRows[index.toString()],
              gameType.name as GameName,
              // Generate a unique ID for the touchpoint based on its index
              `touchpoint_${index}`,
            );
          }
        }

        return touchpointWithContent;
      });

      // Log the processed touchpoints to verify game_config is included
      console.log(
        "Processed touchpoints with game config:",
        processedTouchpoints,
      );

      // Create journey input object for API submission
      const journeyInput = {
        name: journeyInfo.name,
        description: journeyInfo.description,
        team: teamId,
        channel_ids: [], // Will be configured when deploying the journey
        steps: processedTouchpoints,
        // If we have a template selected, include its ID as a reference
        // This tells the API which template this journey is based on
        template_id: selectedTemplate?.id,
      };

      // Log the final journey input that will be sent to the API
      console.log("Journey input being sent to API:", journeyInput);

      if (isEdit) {
        // In edit mode, defer to the parent's onComplete callback
        // Pass processed touchpoints and set skipNavigation to false
        // to allow parent to handle navigation after saving
        onComplete(processedTouchpoints, false);
      } else {
        // In create mode, use the createJourney mutation directly
        await createJourneyMutation.mutateAsync(journeyInput);
        // Then call onComplete with no parameters
        onComplete();
      }
    } catch (err) {
      console.error("Failed to create journey:", err);
      setError("Failed to create journey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if we can proceed to the next step
  // Get validation status with specific validation message
  const getValidationInfo = () => {
    if (showTemplateStep && currentStep === 0) {
      // Template selection step - always valid
      return { isValid: true, message: "" };
    } else if (currentStep === basicInfoStepIndex) {
      // Basic info validation
      if (journeyInfo.name.trim() === "") {
        return {
          isValid: false,
          message: "Please enter a journey name",
        };
      }
      return { isValid: true, message: "" };
    } else if (isTouchpointStep) {
      // Current touchpoint
      const currentTouchpoint = touchpoints[getCurrentTouchpointIndex()];

      // Game type is the only required field
      if (!currentTouchpoint.game_type_id) {
        return {
          isValid: false,
          message: "Please select a game type",
        };
      }
      return { isValid: true, message: "" };
    }

    return { isValid: true, message: "" };
  };

  // Check if current step validation passes
  const canGoNext = () => {
    return getValidationInfo().isValid;
  };

  // Check if we can submit the journey and get appropriate message
  const getSubmitValidationInfo = () => {
    // Journey name is required
    if (journeyInfo.name.trim() === "") {
      return {
        isValid: false,
        message: "Please provide a journey name",
      };
    }

    // At least one touchpoint is required (should always be true due to UI constraints)
    if (touchpoints.length === 0) {
      return {
        isValid: false,
        message: "At least one touchpoint is required",
      };
    }

    // Each touchpoint must have a game type
    const invalidTouchpoints = touchpoints.filter((t) => !t.game_type_id);
    if (invalidTouchpoints.length > 0) {
      return {
        isValid: false,
        message: `${invalidTouchpoints.length} touchpoint${invalidTouchpoints.length > 1 ? "s" : ""} missing game type`,
      };
    }

    return { isValid: true, message: "" };
  };

  const canSubmit = () => {
    return getSubmitValidationInfo().isValid;
  };

  // Loading state
  const isLoading = gameTypesLoading || categoriesLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <WizardContainer
      currentStep={currentStep}
      totalSteps={getTotalSteps()}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onCancel={onCancel}
      onSubmit={handleSubmit}
      onDelete={onDelete}
      isSubmitting={isSubmitting}
      canGoNext={canGoNext()}
      canSubmit={canSubmit()}
      title={isEdit ? "Edit Your Journey" : "Create Your Journey"}
      nextButtonTooltip={getValidationInfo().message}
      submitButtonTooltip={getSubmitValidationInfo().message}
      isEdit={isEdit}
    >
      {/* Template Selection Step */}
      {showTemplateStep && currentStep === 0 && (
        <JourneyTemplatesList
          onTemplateSelect={(template) => {
            // Update wizard state with selected template
            // If a template is selected, pre-fill the journey info with template data
            if (template) {
              // Pre-fill journey info with template data
              const updatedJourneyInfo = {
                ...journeyInfo,
                name: `${template.name} (Copy)`,
                description: template.description || journeyInfo.description,
              };

              // Update the wizard state with template and pre-filled info
              onWizardStateChange({
                ...wizardState,
                selectedTemplate: template,
                journeyInfo: updatedJourneyInfo,
              });
            } else {
              // No template selected, just update the wizard state
              onWizardStateChange({
                ...wizardState,
                selectedTemplate: null,
              });
            }
          }}
        />
      )}

      {/* Journey Basic Info Step */}
      {currentStep === basicInfoStepIndex && (
        <JourneyBasicInfoStep
          values={journeyInfo}
          onChange={handleJourneyInfoChange}
          registerStateCollector={registerStepStateCollector}
        />
      )}

      {isTouchpointStep && (
        <TouchpointStep
          touchpointIndex={getCurrentTouchpointIndex()}
          totalTouchpoints={journeyInfo.touchpointCount}
          touchpoint={touchpoints[getCurrentTouchpointIndex()]}
          onChange={handleTouchpointChange}
          gameTypes={gameTypes}
          categories={categories}
          registerStateCollector={registerStepStateCollector}
          // Pass content rows and handler for the current touchpoint
          contentRows={
            contentRows[getCurrentTouchpointIndex().toString()] || []
          }
          onContentRowsChange={handleContentRowsChange}
        />
      )}

      {isReviewStep && (
        <ReviewStep
          journeyInfo={journeyInfo}
          touchpoints={touchpoints}
          gameTypes={gameTypes}
          onEditJourneyInfo={handleEditJourneyInfo}
          onEditTouchpoint={handleEditTouchpoint}
        />
      )}

      {/* Error message */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}
    </WizardContainer>
  );
};

export default JourneyWizard;
